import React, {
  FC,
  ReactNode,
  createContext,
  memo,
  useContext,
  useMemo,
  useState,
  useRef,
  Dispatch,
  useEffect,
} from 'react';
import {
  setExperimentPaintedDoor,
  setExperimentCosmeticVehicleConcern,
  setExperimentCosmeticExteriorLocation,
  SarStore,
} from '@store/sar-store';
import { setSessionId } from '@components/service-and-repair/utilities';
import { getUserState } from '@carvana/core-auth';
import { AnalyticEvents, Experiments } from '@constants/index';
import { datadogRum } from '@datadog/browser-rum';
import { logExperimentResolution, resolveExperimentEnvironment } from '@utils/experimentTelemetry';
import { bucketExperiment } from '@utils/bucketExperiment';
import { useUserSelections } from '../user-selections';
import { Category, SubCategory, Warranty } from '../../constants';
import { ROUTES, RouteItem } from './types';
import { useMileage } from '../../hooks/use-mileage';
import { Analytics } from '../../../../services/analytics-service';

type HistoryItem = {
  path: ROUTES;
};

const noop = () => {};

export const RouterContext = createContext({
  history: [] as HistoryItem[],
  goToRoute: noop as (path: ROUTES, anchor?: string) => void,
  registerRoute: noop as (routeItem: RouteItem) => void,
  routes: new Map<ROUTES, ReactNode>(),
  currentRoute: null as ROUTES | null,
  isLoading: false,
  loadingText: '',
  setLoadingText: noop as Dispatch<React.SetStateAction<string>>,
  setIsLoading: noop as Dispatch<React.SetStateAction<boolean>>,
  goBack: noop as () => void,
  setError: noop as Dispatch<React.SetStateAction<string>>,
  error: '',
  showBackButton: false as boolean,
  completeFormStep: noop as (route: ROUTES, dataSubset?: any) => void,
  closeModal: noop as any,
});

export const useRouter = () => {
  const context = useContext(RouterContext);
  if (context === undefined) {
    throw new Error('useRouter must be used within a RouterProvider');
  }

  return context;
};

type RouterProviderProps = {
  children: ReactNode | ReactNode[];
  closeModal: any;
};

const DEFAULT_LOADING_TEXT = 'Loading...';

export const RouterProvider: FC<RouterProviderProps> = memo(({ children, closeModal }: any) => {
  const [currentRoute, setCurrentRoute] = useState<ROUTES | null>(ROUTES.LOADING);
  const [showBackButton, setShowBackButton] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loadingText, setLoadingText] = useState<string>(DEFAULT_LOADING_TEXT);
  const [error, setError] = useState<string>('');
  const routes = useRef<Map<ROUTES, ReactNode>>(new Map());
  const history = useRef<HistoryItem[]>([]);
  const nonCosmeticExperimentBucket = useRef<string | null>(null);
  const hasInitialized = useRef(false);
  const { userFeedData, userSelections, updateUserSelections, clearUserSelections } = useUserSelections();
  const { savedMileage, validateMileage, deleteSavedMileage } = useMileage();

  const handleBackButton = (path: ROUTES) => {
    const hideBackButtonOnRoutes = [
      ROUTES.CATEGORIES,
      ROUTES.COVERAGE,
      ROUTES.LOADING,
      ROUTES.PAINTED_DOOR,
    ];
    setShowBackButton(!hideBackButtonOnRoutes.includes(path) || isLoading);
  };

  const goToRoute = (path: ROUTES) => {
    const route = routes.current.get(path);
    if (!route && process.env.LOCAL !== 'true') {
      console.log(`Route not found ${path}`);
      return;
    }

    handleBackButton(path);
    setCurrentRoute(path);
    history.current.push({ path });
  };

  const initializeApp = async () => {
    const { saleEffectiveDate, vehicleType } = userFeedData || {};
    if (Object.keys(userFeedData).length === 0 && !saleEffectiveDate) {
      return;
    }

    if (hasInitialized.current) {
      datadogRum.addAction('sar-initialize-app-blocked-rerun', {
        currentRoute,
        hasCategory: !!userSelections.category,
        category: userSelections.category,
        sarSessionId: SarStore.sarSessionId,
      });
      return;
    }
    hasInitialized.current = true;

    setSessionId();
    setShowBackButton(false);
    history.current = [];
    nonCosmeticExperimentBucket.current = null; // Clear cached experiment bucket
    clearUserSelections();

    if (userFeedData && Object.keys(userFeedData).length > 0 && !saleEffectiveDate) {
      Analytics.trackEvent(AnalyticEvents.sarsNoSaleEffectiveDate);
      datadogRum.addAction(AnalyticEvents.sarsNoSaleEffectiveDate);
      goToRoute(ROUTES.CATEGORIES);
      return;
    }

    const isSalesEffectiveDateAfterPaintedDoorExperimentGoLiveDate =
      saleEffectiveDate && new Date(saleEffectiveDate) > new Date('2025-09-25');
    if (isSalesEffectiveDateAfterPaintedDoorExperimentGoLiveDate) {
      const assignedBucket = await bucketExperiment(
        {
          name: Experiments.PAINTED_DOOR,
          identifier: getUserState()?.user?.user_id,
          environment: resolveExperimentEnvironment(),
          skipBucketing: !isSalesEffectiveDateAfterPaintedDoorExperimentGoLiveDate,
        },
        'initializeApp painted-door bucketing failed'
      );
      Analytics.trackEvent(AnalyticEvents.paintedDoorExperiment, { bucket: assignedBucket });
      datadogRum.addAction(AnalyticEvents.paintedDoorExperiment, { bucket: assignedBucket });
      setExperimentPaintedDoor(assignedBucket);
      if (assignedBucket?.includes('treatment')) {
        goToRoute(ROUTES.PAINTED_DOOR);
        return;
      }
    }

    goToRoute(ROUTES.CATEGORIES);
  };

  useEffect(() => {
    (async () => {
      initializeApp();
    })();
  }, [userFeedData]);

  const registerRoute = (routeItem: RouteItem) => {
    routes.current.set(routeItem.path, routeItem.component);
  };

  const goBack = () => {
    if (history.current.length < 2) return;
    const previousRoute = history.current[history.current.length - 2];
    if (previousRoute) {
      history.current = history.current.slice(0, history.current.length - 1);
      handleBackButton(previousRoute.path);
      setCurrentRoute(previousRoute.path);
    }
  };

  const goToMileageRoute = () => {
    if (savedMileage) {
      const mileageError = validateMileage(savedMileage);
      if (mileageError) {
        deleteSavedMileage();
        goToRoute(ROUTES.MILEAGE);
      } else {
        updateUserSelections({ mileage: Number(savedMileage) });
        goToRoute(ROUTES.COVERAGE);
      }
    } else {
      goToRoute(ROUTES.MILEAGE);
    }
  };

  const completeFormStep = async (completedRoute: ROUTES, dataSubset?: any) => {
    const subsetTitle = dataSubset?.title;
    const { coverage: products, fuelDescription, reportedConcerns } = userFeedData;
    // products is an array of coverages pre-filtered to exclude expired coverages
    const hasCarvanaCarePremier = products?.find((coverage: any) => coverage.type === Warranty.CarvanaCarePremier);
    const hasCarvanaCareCosmetic = products?.find((coverage: any) => coverage.type === Warranty.CarvanaCareCosmetic);
    const under30Days = products?.find((coverage: any) => coverage.type === Warranty.ThirtyDay);
    const within7Days = products?.find((coverage: any) => coverage.type === Warranty.SevenDay);
    const hasAsrExtension = products?.find((coverage: any) => coverage.type === Warranty.ASRExtension);

    const glassConcernCount =
      reportedConcerns?.filter((concern: any) => concern.subCategory === SubCategory.Glass).length || 0;
    const cosmeticConcernCount =
      reportedConcerns?.filter(
        (concern: any) => concern.subCategory === SubCategory.Exterior || concern.subCategory === SubCategory.Interior
      ).length || 0;

    switch (completedRoute) {
      case ROUTES.PAINTED_DOOR: {
        goToRoute(ROUTES.CATEGORIES);
        break;
      }
      case ROUTES.CATEGORIES:
        if (dataSubset?.categories) {
          const isBatteryCategory = subsetTitle === Category.Battery;
          const missingFuelDescription = !fuelDescription;

          if (isBatteryCategory && fuelDescription) {
            const isNotGas = ['hybrid', 'electric', 'plug-in hybrid'].some(
              (x: string) => fuelDescription.toLowerCase() === x
            );
            const subcategory = isNotGas ? SubCategory.ElectricOrHybrid : SubCategory.Gas;
            updateUserSelections({ subcategory });
          }

          if (isBatteryCategory && under30Days) {
            goToMileageRoute();
            break;
          }

          if (isBatteryCategory && !missingFuelDescription) {
            goToMileageRoute();
            break;
          }

          goToRoute(ROUTES.SUB_CATEGORIES);
        } else {
          goToMileageRoute();
        }
        break;
      case ROUTES.SUB_CATEGORIES: {
        const within7DaysOrHasExtension: boolean = within7Days || hasAsrExtension;

        if (subsetTitle === SubCategory.Glass) {
          if (!within7DaysOrHasExtension || glassConcernCount >= 1) {
            goToRoute(ROUTES.MILEAGE);
          } else {
            goToRoute(ROUTES.LOCATIONS);
          }
          break;
        }

        if (
          subsetTitle === SubCategory.Exterior &&
          !within7DaysOrHasExtension &&
          (hasCarvanaCarePremier || hasCarvanaCareCosmetic)
        ) {
          goToRoute(ROUTES.SUB_SUB_CATEGORIES);
          break;
        }

        if (subsetTitle === SubCategory.Exterior && !within7DaysOrHasExtension && !hasCarvanaCarePremier) {
          goToMileageRoute();
          break;
        }

        if (
          (subsetTitle === SubCategory.Exterior || subsetTitle === SubCategory.Interior) &&
          within7DaysOrHasExtension
        ) {
          if (cosmeticConcernCount >= 3) {
            goToMileageRoute();
          } else if (within7DaysOrHasExtension) {
            // Bucket the exterior-cosmetic spinner-location experiment before rendering
            // the locations step, so it reads treatment synchronously (no list flash).
            // Only bucket when the user clicks Exterior AND is within the 7-day money-back
            // guarantee (7DMBG); an ASR extension alone no longer qualifies.
            if (subsetTitle === SubCategory.Exterior && within7Days) {
              const { env } = userFeedData;
              const resolvedEnvironment = resolveExperimentEnvironment(env);
              const identifier = getUserState()?.user?.user_id;
              const assignedBucket = await bucketExperiment(
                {
                  name: Experiments.COSMETIC_EXTERIOR_LOCATION,
                  identifier,
                  environment: resolvedEnvironment,
                },
                'ROUTES.SUB_CATEGORIES cosmetic-exterior-location bucketing failed'
              );
              logExperimentResolution({
                experiment: Experiments.COSMETIC_EXTERIOR_LOCATION,
                environment: resolvedEnvironment,
                identifier,
                bucket: assignedBucket,
              });
              Analytics.trackEvent(AnalyticEvents.cosmeticExteriorLocationExperiment, { bucket: assignedBucket });
              datadogRum.addAction(AnalyticEvents.cosmeticExteriorLocationExperiment, { bucket: assignedBucket });
              setExperimentCosmeticExteriorLocation(assignedBucket);
            }
            goToRoute(ROUTES.LOCATIONS);
          } else {
            goToRoute(ROUTES.IMAGE_UPLOAD);
          }
          break;
        }

        if (dataSubset?.categories) {
          goToRoute(ROUTES.SUB_SUB_CATEGORIES);
        } else {
          const isNonCosmetic = userSelections.category !== Category.Cosmetic;
          const hasLimitedWarranty = products?.find((coverage: any) => coverage.type === Warranty.LimitedWarranty);
          if (isNonCosmetic && hasLimitedWarranty) {
            // Check cache first to avoid duplicate GetExperiment calls
            if (!nonCosmeticExperimentBucket.current) {
              const { env } = userFeedData;
              const assignedBucket = await bucketExperiment(
                {
                  name: Experiments.NON_COSMETIC_VEHICLE_CONCERN_DETAILS,
                  identifier: getUserState()?.user?.user_id,
                  environment: resolveExperimentEnvironment(env),
                },
                'ROUTES.SUB_CATEGORIES non-cosmetic bucketing failed'
              );
              nonCosmeticExperimentBucket.current = assignedBucket;
              Analytics.trackEvent(AnalyticEvents.vehicleConcernDetailsNonCosmeticExperiment, {
                bucket: assignedBucket,
              });
              datadogRum.addAction(AnalyticEvents.vehicleConcernDetailsNonCosmeticExperiment, {
                bucket: assignedBucket,
              });
            }

            const isTreatment = nonCosmeticExperimentBucket.current?.includes('treatment');
            if (isTreatment) {
              goToRoute(ROUTES.VEHICLE_CONCERN_DETAILS);
              break;
            }
          }
          goToMileageRoute();
        }
        break;
      }
      case ROUTES.LOCATIONS: {
        const isCosmeticCategory = userSelections.category === Category.Cosmetic;
        if (isCosmeticCategory) {
          const { env } = userFeedData;
          const assignedBucket = await bucketExperiment(
            {
              name: Experiments.COSMETIC_VEHICLE_CONCERN_DETAILS,
              identifier: getUserState()?.user?.user_id,
              environment: resolveExperimentEnvironment(env),
            },
            'ROUTES.LOCATIONS experiment bucketing failed'
          );
          setExperimentCosmeticVehicleConcern(assignedBucket);
          Analytics.trackEvent(AnalyticEvents.vehicleConcernDetailsCosmeticExperiment, { bucket: assignedBucket });
          datadogRum.addAction(AnalyticEvents.vehicleConcernDetailsCosmeticExperiment, { bucket: assignedBucket });
        }
        goToRoute(ROUTES.IMAGE_UPLOAD);
        break;
      }
      case ROUTES.SUB_SUB_CATEGORIES: {
        const isNonCosmetic = userSelections.category !== Category.Cosmetic;
        const hasActiveLimitedWarranty = products?.find((coverage: any) => coverage.type === Warranty.LimitedWarranty);
        if (isNonCosmetic && hasActiveLimitedWarranty) {
          // Use cached bucket to avoid duplicate GetExperiment calls
          if (!nonCosmeticExperimentBucket.current) {
            const { env } = userFeedData;
            const assignedBucket = await bucketExperiment(
              {
                name: Experiments.NON_COSMETIC_VEHICLE_CONCERN_DETAILS,
                identifier: getUserState()?.user?.user_id,
                environment: resolveExperimentEnvironment(env),
              },
              'ROUTES.SUB_SUB_CATEGORIES non-cosmetic bucketing failed'
            );
            nonCosmeticExperimentBucket.current = assignedBucket;
            Analytics.trackEvent(AnalyticEvents.vehicleConcernDetailsNonCosmeticExperiment, {
              bucket: assignedBucket,
            });
            datadogRum.addAction(AnalyticEvents.vehicleConcernDetailsNonCosmeticExperiment, { bucket: assignedBucket });
          }

          const isTreatment = nonCosmeticExperimentBucket.current?.includes('treatment');
          if (isTreatment) {
            goToRoute(ROUTES.VEHICLE_CONCERN_DETAILS);
            break;
          }
        }
        goToMileageRoute();
        break;
      }
      case ROUTES.VEHICLE_CONCERN_DETAILS:
        goToRoute(ROUTES.MILEAGE);
        break;
      case ROUTES.MILEAGE:
        goToRoute(ROUTES.COVERAGE);
        break;
      case ROUTES.IMAGE_UPLOAD:
        goToRoute(ROUTES.COVERAGE);
        break;
      default:
        break;
    }
  };

  const value = useMemo(
    () => ({
      history: history.current,
      goToRoute,
      registerRoute,
      routes: routes.current,
      currentRoute,
      isLoading,
      setIsLoading,
      goBack,
      error,
      setError,
      loadingText,
      setLoadingText,
      showBackButton,
      completeFormStep,
      closeModal,
    }),
    [showBackButton, currentRoute, error, userFeedData, savedMileage, userSelections]
  );

  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
});
